/**
 * Executive View API client.
 *
 * Options (brands + their AI-enabled/tagged scenarios) come from the
 * main API; prompts and deterministic query execution come from
 * Agentic-API.
 * Everything routes through services/API.ts so it inherits Bearer-token
 * forwarding (MSAL or SKIP_AUTH bypass) and HTTPError semantics.
 */
import { API_TYPE, create, load } from 'src/services/API';

import {
  ExecutiveOptionsResponse,
  ExecutivePromptItem,
  ExecutiveQueryRecentResponse,
  ExecutiveQueryResponse,
  ExecutiveTornadoPromptItem,
  TornadoRunStatus,
  WhatIfOptions,
  WhatIfProposeRequest,
  WhatIfProposeResponse,
  WhatIfRecentResponse,
  WhatIfRunStatus,
} from './types';

export async function getExecutiveOptions(
  clientId: number
): Promise<ExecutiveOptionsResponse> {
  return load(
    `/v2/clients/${clientId}/executive/options`,
    undefined,
    API_TYPE.DEFAULT
  ) as Promise<ExecutiveOptionsResponse>;
}

export async function getExecutivePrompts(
  modelInstanceId: number
): Promise<{ prompts: ExecutivePromptItem[] }> {
  return load(
    `/v2/executive/prompts?model_instance_id=${modelInstanceId}`,
    undefined,
    API_TYPE.AGENTIC
  ) as Promise<{ prompts: ExecutivePromptItem[] }>;
}

export async function runExecutiveQuery(
  promptId: number,
  scenarioIds: number[],
  scenarioNames: string[],
  record: boolean
): Promise<ExecutiveQueryResponse> {
  return create<ExecutiveQueryResponse>(
    '/v2/executive/query',
    {
      prompt_id: promptId,
      scenario_ids: scenarioIds,
      scenario_names: scenarioNames,
      record,
    },
    API_TYPE.AGENTIC
  );
}

/**
 * QF RECENT tab (design doc executive-view-design/EXEC_WHATIF_v1_
 * design.md §5, §8). Rows are already deduped by prompt server-side,
 * newest first, cap 10. Backend route is contractually fixed but may
 * not be landed yet in the Agentic-API tree — implemented to contract;
 * a fetch failure is treated as "no recents" by the caller (calm
 * failure, §6 tone), never a blocking error.
 */
export async function getQueryRecent(
  instanceId: number
): Promise<ExecutiveQueryRecentResponse> {
  return load(
    `/v2/executive/query/recent?model_instance_id=${instanceId}`,
    undefined,
    API_TYPE.AGENTIC
  ) as Promise<ExecutiveQueryRecentResponse>;
}

/**
 * What-If Analysis client (design doc executive-view-design/
 * EXEC_WHATIF_v1_design.md §7). Backend endpoints are contractually
 * fixed but not yet landed; shapes live in ./types and get reconciled
 * there if the backend's exact wrapper keys differ at integration.
 */

export async function getWhatIfOptions(
  instanceId: number
): Promise<WhatIfOptions> {
  return load(
    `/v2/executive/whatif/options?model_instance_id=${instanceId}`,
    undefined,
    API_TYPE.AGENTIC
  ) as Promise<WhatIfOptions>;
}

export async function proposeWhatIf(
  body: WhatIfProposeRequest
): Promise<WhatIfProposeResponse> {
  return create<WhatIfProposeResponse>(
    '/v2/executive/whatif/propose',
    body,
    API_TYPE.AGENTIC
  );
}

// approve/discard kick off (approve) or terminate (discard) a run but
// don't return the run itself — the backend responds `{status}` only
// (F1). Callers re-poll `getWhatIfRun` for the actual run state.
export async function approveWhatIf(
  planId: string
): Promise<{ status: string }> {
  return create<{ status: string }>(
    `/v2/executive/whatif/${planId}/approve`,
    {},
    API_TYPE.AGENTIC
  );
}

export async function getWhatIfRun(planId: string): Promise<WhatIfRunStatus> {
  return load(
    `/v2/executive/whatif/${planId}`,
    undefined,
    API_TYPE.AGENTIC
  ) as Promise<WhatIfRunStatus>;
}

export async function discardWhatIf(
  planId: string
): Promise<{ status: string }> {
  return create<{ status: string }>(
    `/v2/executive/whatif/${planId}/discard`,
    {},
    API_TYPE.AGENTIC
  );
}

// Soft-hide a FAILED run from the recents list (2026-08-23): the run
// stays `failed` in the ledger; the backend merges dismissed_at into
// Progress and the recents listing filters on it. 409 for any
// non-failed status.
export async function dismissWhatIfRun(
  planId: string
): Promise<{ dismissed: boolean }> {
  return create<{ dismissed: boolean }>(
    `/v2/executive/whatif/${planId}/dismiss`,
    {},
    API_TYPE.AGENTIC
  );
}

export async function getWhatIfRecent(
  instanceId: number
): Promise<WhatIfRecentResponse> {
  return load(
    `/v2/executive/whatif/recent?model_instance_id=${instanceId}`,
    undefined,
    API_TYPE.AGENTIC
  ) as Promise<WhatIfRecentResponse>;
}

/**
 * Tornado destination client (Agentic-API /v2/executive/tornado/*).
 * Kickoff + poll: `runTornado` mints a run (AgentPlan) and returns its
 * plan_id; the answer view polls `getTornadoRun` until completed/failed.
 */

export async function getExecutiveTornadoPrompts(
  modelInstanceId: number
): Promise<{ prompts: ExecutiveTornadoPromptItem[] }> {
  return load(
    `/v2/executive/prompts?model_instance_id=${modelInstanceId}&tool=executive_tornado`,
    undefined,
    API_TYPE.AGENTIC
  ) as Promise<{ prompts: ExecutiveTornadoPromptItem[] }>;
}

export async function runTornado(
  promptId: number,
  scenarioAId: number,
  scenarioBId: number
): Promise<{ plan_id: string; status: string }> {
  return create<{ plan_id: string; status: string }>(
    '/v2/executive/tornado/run',
    {
      prompt_id: promptId,
      scenario_a_id: scenarioAId,
      scenario_b_id: scenarioBId,
    },
    API_TYPE.AGENTIC
  );
}

export async function getTornadoRun(planId: string): Promise<TornadoRunStatus> {
  return load(
    `/v2/executive/tornado/${planId}`,
    undefined,
    API_TYPE.AGENTIC
  ) as Promise<TornadoRunStatus>;
}

// Same row shape as QF recents (deduped by prompt server-side, newest
// first, cap 10) so QuestionLibrary's RECENT tab renders both unchanged.
export async function getTornadoRecent(
  instanceId: number
): Promise<ExecutiveQueryRecentResponse> {
  return load(
    `/v2/executive/tornado/recent?model_instance_id=${instanceId}`,
    undefined,
    API_TYPE.AGENTIC
  ) as Promise<ExecutiveQueryRecentResponse>;
}
